// Данные для библиотеки заболеваний с научно-обоснованными статьями
export interface Disease {
  name: string;
  category: string;
  description: string;
  symptoms: string[];
  clinicalPresentation: string;
  complications: string[];
  treatment: string;
  prevention: string;
  epidemiology: string;
  pathophysiology: string;
  diagnosis: string;
  differentialDiagnosis: string[];
  prognosis: string;
  references: string[];
  rashDescription: string;
  images: string[];
  severity: 'low' | 'medium' | 'high';
  ageGroup: string;
  incubation: string;
  contagious: string;
  source?: string;
}

export const infectiousDiseases: Disease[] = [
  {
    name: 'Корь',
    category: 'вирусные',
    description: 'Острое высококонтагиозное вирусное заболевание, вызываемое вирусом кори (Morbillivirus), характеризующееся катаральными явлениями, пятнами Бельского-Филатова-Коплика и этапной сыпью.',
    symptoms: [
      'Повышение температуры до 39-40°C',
      'Катаральные явления (кашель, насморк, конъюнктивит)',
      'Пятна Бельского-Филатова-Коплика на слизистой щек',
      'Этапная сыпь: лицо → туловище → конечности',
      'Пигментация после исчезновения сыпи'
    ],
    clinicalPresentation: 'Заболевание протекает в 4 периода: инкубационный (8-12 дней), катаральный (3-4 дня), период высыпаний (3-4 дня) и период пигментации. Характерны пятна Бельского-Филатова-Коплика на слизистой щек, появляющиеся за 1-2 дня до сыпи. Сыпь крупнопятнистая, папулезная, склонная к слиянию, появляется поэтапно.',
    complications: ['Пневмония (6-7%)', 'Энцефалит (0.1%)', 'Кератит (0.1%)', 'Отит (7-9%)', 'Подострый склерозирующий панэнцефалит'],
    treatment: 'Симптоматическое лечение: жаропонижающие (парацетамол, ибупрофен), витамин А 200,000 МЕ однократно детям старше 12 месяцев, обильное питье, постельный режим. При осложнениях - антибактериальная терапия.',
    prevention: 'Активная иммунизация живой аттенуированной вакциной против кори в 12 месяцев и 6 лет. Контактным лицам - введение иммуноглобулина в течение 72 часов.',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный. Контагиозность 95-98%. Восприимчивость всеобщая. Сезонность - зимне-весенний период.',
    pathophysiology: 'Вирус проникает через слизистые оболочки дыхательных путей, размножается в лимфоидной ткани, затем гематогенно распространяется по организму, поражая кожу, слизистые, ЦНС.',
    diagnosis: 'Клиническая картина, серологическое исследование (IgM, IgG), ПЦР, выделение вируса из носоглоточных смывов.',
    differentialDiagnosis: ['Краснуха', 'Скарлатина', 'Ветряная оспа', 'Аллергические реакции', 'Инфекционный мононуклеоз'],
    prognosis: 'При неосложненном течении - благоприятный. При развитии энцефалита - серьезный, летальность до 15%.',
    references: [
      'AAP Red Book 2021-2024',
      'Клинические рекомендации МЗ РФ "Корь" 2021',
      'WHO Measles Fact Sheet 2023',
      'CDC Measles Guidelines 2023'
    ],
    rashDescription: 'Крупнопятнистая папулезная сыпь, склонная к слиянию, появляется поэтапно',
    images: [
      '/images/atlas/measles-1.jpg',
      '/images/atlas/measles-2.jpg',
      '/images/atlas/measles-3.jpg'
    ],
    severity: 'high',
    ageGroup: '1-15 лет',
    incubation: '8-12 дней',
    contagious: '4 дня до сыпи + 4 дня после',
    source: 'Научная литература'
  },
  {
    name: 'Краснуха',
    category: 'вирусные',
    description: 'Острое вирусное заболевание, вызываемое вирусом краснухи (Rubivirus), характеризующееся мелкопятнистой сыпью, лимфаденопатией и умеренной интоксикацией.',
    symptoms: [
      'Умеренное повышение температуры (37.5-38.5°C)',
      'Увеличение затылочных и заднешейных лимфоузлов',
      'Мелкопятнистая сыпь на лице и туловище',
      'Сыпь исчезает без пигментации',
      'У взрослых может протекать тяжелее'
    ],
    clinicalPresentation: 'Заболевание протекает легко у детей, тяжелее у взрослых. Характерна лимфаденопатия затылочных и заднешейных лимфоузлов. Сыпь мелкопятнистая, розовая, не склонная к слиянию, появляется в первый день болезни, исчезает через 2-3 дня без пигментации.',
    complications: ['Артрит (у взрослых женщин)', 'Энцефалит (1:6000)', 'Тромбоцитопения', 'Врожденная краснуха у беременных (синдром Грегга)'],
    treatment: 'Симптоматическое лечение: жаропонижающие при необходимости, постельный режим, обильное питье. Изоляция больного до 5 дней после появления сыпи.',
    prevention: 'Активная иммунизация живой аттенуированной вакциной против краснухи в составе MMR в 12 месяцев и 6 лет. Особенно важно для женщин детородного возраста.',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный. Контагиозность 50-70%. Восприимчивость высокая. Сезонность - весенний период.',
    pathophysiology: 'Вирус проникает через слизистые оболочки дыхательных путей, размножается в лимфоидной ткани, затем гематогенно распространяется, поражая кожу и лимфоузлы.',
    diagnosis: 'Клиническая картина, серологическое исследование (IgM, IgG), ПЦР, выделение вируса из носоглоточных смывов.',
    differentialDiagnosis: ['Корь', 'Скарлатина', 'Ветряная оспа', 'Аллергические реакции', 'Энтеровирусная инфекция'],
    prognosis: 'У детей - благоприятный. У взрослых - более тяжелое течение. При врожденной краснухе - серьезные пороки развития.',
    references: [
      'AAP Red Book 2021-2024',
      'Клинические рекомендации МЗ РФ "Краснуха" 2021',
      'WHO Rubella Fact Sheet 2023',
      'CDC Rubella Guidelines 2023'
    ],
    rashDescription: 'Мелкопятнистая розовая сыпь, не склонная к слиянию, исчезает без пигментации',
    images: [
      '/images/atlas/rubella-1.jpg',
      '/images/atlas/rubella-2.jpg',
      '/images/atlas/rubella-3.jpg'
    ],
    severity: 'medium',
    ageGroup: '2-14 лет',
    incubation: '14-21 день',
    contagious: '7 дней до сыпи + 7 дней после',
    source: 'Научная литература'
  },
  {
    name: 'Скарлатина',
    category: 'бактериальные',
    description: 'Острое инфекционное заболевание, вызываемое β-гемолитическим стрептококком группы А, продуцирующим эритрогенный токсин, характеризующееся ангиной, мелкоточечной сыпью и интоксикацией.',
    symptoms: [
      'Высокая температура (38-40°C)',
      'Ангина с яркой гиперемией зева',
      'Малиновый язык (с 3-4 дня)',
      'Мелкоточечная сыпь на гиперемированном фоне',
      'Шелушение кожи после исчезновения сыпи'
    ],
    clinicalPresentation: 'Заболевание начинается остро с повышения температуры, головной боли, рвоты. Ангина с яркой гиперемией зева, миндалин. Сыпь появляется в первый день болезни, мелкоточечная, ярко-красная, на гиперемированном фоне, особенно выражена в складках кожи.',
    complications: ['Ревматизм (2-3%)', 'Гломерулонефрит (1-2%)', 'Миокардит', 'Отит (7-9%)', 'Синусит', 'Лимфаденит'],
    treatment: 'Антибактериальная терапия: бензилпенициллин 50,000-100,000 ЕД/кг/сут в/м 7-10 дней, амоксициллин 50 мг/кг/сут внутрь. При аллергии - макролиды. Симптоматическое лечение.',
    prevention: 'Изоляция больного до 10 дней после начала лечения. Контактным лицам - медицинское наблюдение 7 дней. Специфическая профилактика не разработана.',
    epidemiology: 'Источник - больной человек или носитель. Путь передачи - воздушно-капельный, контактный. Восприимчивость высокая у детей 3-10 лет. Сезонность - осенне-зимний период.',
    pathophysiology: 'Стрептококк группы А продуцирует эритрогенный токсин, вызывающий системную воспалительную реакцию, поражение сосудов, развитие характерной сыпи.',
    diagnosis: 'Клиническая картина, бактериологическое исследование (мазок из зева), экспресс-тесты на стрептококк, серологическое исследование (АСЛ-О).',
    differentialDiagnosis: ['Корь', 'Краснуха', 'Ветряная оспа', 'Аллергические реакции', 'Инфекционный мононуклеоз'],
    prognosis: 'При своевременном лечении - благоприятный. При развитии осложнений - серьезный, требует длительного наблюдения.',
    references: [
      'AAP Red Book 2021-2024',
      'Клинические рекомендации МЗ РФ "Стрептококковая инфекция" 2021',
      'WHO Scarlet Fever Guidelines 2023',
      'CDC Group A Strep Guidelines 2023'
    ],
    rashDescription: 'Мелкоточечная ярко-красная сыпь на гиперемированном фоне, особенно выражена в складках кожи',
    images: [
      '/images/atlas/scarlet-fever-1.jpg',
      '/images/atlas/scarlet-fever-2.jpg',
      '/images/atlas/scarlet-fever-3.jpg'
    ],
    severity: 'high',
    ageGroup: '3-10 лет',
    incubation: '1-3 дня',
    contagious: '24 часа после начала лечения',
    source: 'Научная литература'
  },
  {
    name: 'Ветряная оспа',
    category: 'вирусные',
    description: 'Острое высококонтагиозное вирусное заболевание, вызываемое вирусом ветряной оспы (Varicella zoster virus, VZV), характеризующееся везикулезной сыпью и умеренной интоксикацией.',
    symptoms: [
      'Повышение температуры (37.5-39°C)',
      'Везикулезная сыпь по всему телу',
      'Зуд кожи различной интенсивности',
      'Сыпь появляется волнами (2-3 волны)',
      'Корочки после вскрытия пузырьков'
    ],
    clinicalPresentation: 'Заболевание начинается с продромальных явлений (слабость, недомогание), затем появляется сыпь. Характерен полиморфизм сыпи: пятна, папулы, везикулы, корочки одновременно. Сыпь появляется волнами, сопровождается зудом.',
    complications: ['Бактериальные инфекции кожи (2-5%)', 'Пневмония (1-2%)', 'Энцефалит (0.1%)', 'Синдром Рея', 'Геморрагическая форма'],
    treatment: 'Симптоматическое лечение: жаропонижающие, антигистаминные при зуде, обработка элементов сыпи антисептиками. При тяжелом течении - ацикловир 20 мг/кг 4 раза в день 5 дней.',
    prevention: 'Активная иммунизация живой аттенуированной вакциной против ветряной оспы в 12 месяцев и 6 лет. Контактным лицам - введение иммуноглобулина в течение 96 часов.',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный, контактный. Контагиозность 90-95%. Восприимчивость всеобщая. Сезонность - зимне-весенний период.',
    pathophysiology: 'Вирус проникает через слизистые оболочки дыхательных путей, размножается в лимфоидной ткани, затем гематогенно распространяется, поражая кожу и слизистые.',
    diagnosis: 'Клиническая картина, серологическое исследование (IgM, IgG), ПЦР, выделение вируса из содержимого везикул.',
    differentialDiagnosis: ['Герпетическая инфекция', 'Импетиго', 'Аллергические реакции', 'Энтеровирусная инфекция', 'Опоясывающий лишай'],
    prognosis: 'У детей - благоприятный. У взрослых и иммунокомпрометированных - более тяжелое течение. После перенесенного заболевания - пожизненный иммунитет.',
    references: [
      'AAP Red Book 2021-2024',
      'Клинические рекомендации МЗ РФ "Ветряная оспа" 2021',
      'WHO Varicella Fact Sheet 2023',
      'CDC Chickenpox Guidelines 2023'
    ],
    rashDescription: 'Полиморфная сыпь: пятна, папулы, везикулы, корочки одновременно, появляется волнами',
    images: [
      '/images/atlas/chickenpox-1.jpg',
      '/images/atlas/chickenpox-2.jpg',
      '/images/atlas/chickenpox-3.jpg'
    ],
    severity: 'medium',
    ageGroup: '1-10 лет',
    incubation: '10-21 день',
    contagious: '1-2 дня до сыпи + 5-7 дней после',
    source: 'Научная литература'
  },
  {
    name: 'Розеола (внезапная экзантема)',
    category: 'вирусные',
    description: 'Острое вирусное заболевание, вызываемое вирусом герпеса 6 типа',
    symptoms: [
      'Высокая температура в течение 3-4 дней',
      'Отсутствие катаральных явлений',
      'Появление сыпи после снижения температуры',
      'Сыпь локализуется на туловище и шее',
      'Быстрое исчезновение сыпи'
    ],
    clinicalPresentation: 'Заболевание начинается с высокой лихорадки (39-40°C) в течение 3-4 дней без катаральных явлений. После снижения температуры появляется мелкопятнистая розовая сыпь на туловище и шее.',
    complications: ['Фебрильные судороги', 'Менингоэнцефалит (редко)'],
    treatment: 'Симптоматическое лечение, жаропонижающие (парацетамол, ибупрофен)',
    prevention: 'Избегание контакта с больными, соблюдение гигиены',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный. Восприимчивость высокая у детей 6 мес - 2 года.',
    pathophysiology: 'Вирус герпеса 6 типа проникает через слизистые оболочки, размножается в лимфоидной ткани, затем гематогенно распространяется.',
    diagnosis: 'Клиническая картина, серологическое исследование (IgM, IgG), ПЦР',
    differentialDiagnosis: ['Корь', 'Краснуха', 'Аллергические реакции', 'Энтеровирусная инфекция'],
    prognosis: 'Благоприятный. Осложнения редки.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Мелкопятнистая розовая сыпь, не склонная к слиянию',
    images: [
      '/images/atlas/roseola-1.jpg',
      '/images/atlas/roseola-2.jpg',
      '/images/atlas/roseola-3.jpg'
    ],
    severity: 'low',
    ageGroup: '6 мес - 2 года',
    incubation: '5-15 дней',
    contagious: 'Во время лихорадки',
    source: 'Научная литература'
  },
  {
    name: 'Парвовирусная инфекция (пятая болезнь)',
    category: 'вирусные',
    description: 'Инфекционное заболевание, вызываемое парвовирусом B19',
    symptoms: [
      'Умеренное повышение температуры',
      'Слабость, недомогание',
      'Характерная сыпь на лице ("симптом пощечины")',
      'Кружевная сыпь на туловище и конечностях',
      'Артралгии у взрослых'
    ],
    clinicalPresentation: 'Заболевание начинается с продромальных явлений, затем появляется характерная сыпь на лице в виде "симптома пощечины", затем кружевная сыпь на туловище и конечностях.',
    complications: ['Апластический криз', 'Артрит', 'Врожденная инфекция'],
    treatment: 'Симптоматическое лечение, покой',
    prevention: 'Избегание контакта с больными, соблюдение гигиены',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный, контактный. Восприимчивость высокая у детей 4-10 лет.',
    pathophysiology: 'Парвовирус B19 поражает эритроидные клетки-предшественники, вызывая временную аплазию эритроцитов.',
    diagnosis: 'Клиническая картина, серологическое исследование (IgM, IgG), ПЦР.',
    differentialDiagnosis: ['Корь', 'Краснуха', 'Аллергические реакции', 'Скарлатина'],
    prognosis: 'Благоприятный. У иммунокомпрометированных - риск апластического криза.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Кружевная сетчатая сыпь, особенно выраженная на лице',
    images: [
      '/images/atlas/parvovirus-1.jpg',
      '/images/atlas/parvovirus-2.jpg',
      '/images/atlas/parvovirus-3.jpg'
    ],
    severity: 'low',
    ageGroup: '4-10 лет',
    incubation: '4-14 дней',
    contagious: 'До появления сыпи',
    source: 'Научная литература'
  },
  {
    name: 'Энтеровирусная инфекция',
    category: 'вирусные',
    description: 'Группа заболеваний, вызываемых энтеровирусами',
    symptoms: [
      'Повышение температуры',
      'Головная боль, слабость',
      'Полиморфная сыпь',
      'Возможны симптомы поражения ЦНС',
      'Герпангина, миалгии'
    ],
    clinicalPresentation: 'Заболевание протекает с лихорадкой, интоксикацией, полиморфной сыпью. Возможны различные клинические формы: герпангина, миалгия, менингит.',
    complications: ['Менингит', 'Энцефалит', 'Миокардит', 'Параличи'],
    treatment: 'Симптоматическое лечение, дезинтоксикация, при тяжелых формах - специфическая терапия',
    prevention: 'Соблюдение гигиены, избегание контакта с больными, вакцинация против полиомиелита',
    epidemiology: 'Источник - больной человек, носитель. Путь передачи - фекально-оральный, воздушно-капельный. Сезонность - летне-осенний период.',
    pathophysiology: 'Энтеровирусы проникают через слизистые ЖКТ и дыхательных путей, размножаются в лимфоидной ткани, затем гематогенно распространяются.',
    diagnosis: 'Клиническая картина, серологическое исследование, ПЦР, выделение вируса из фекалий, ликвора.',
    differentialDiagnosis: ['Корь', 'Краснуха', 'Ветряная оспа', 'Аллергические реакции', 'Менингиты другой этиологии'],
    prognosis: 'При неосложненных формах - благоприятный. При менингите, энцефалите - серьезный.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Полиморфная сыпь: пятнистая, папулезная, везикулезная',
    images: [
      '/images/atlas/enterovirus-1.jpg',
      '/images/atlas/enterovirus-2.jpg',
      '/images/atlas/enterovirus-3.jpg'
    ],
    severity: 'medium',
    ageGroup: '1-5 лет',
    incubation: '3-7 дней',
    contagious: '1-2 недели',
    source: 'Научная литература'
  },
  {
    name: 'Амоксициллин-ассоциированная сыпь при мононуклеозе',
    category: 'лекарственные',
    description: 'Сыпь, возникающая при назначении амоксициллина на фоне инфекционного мононуклеоза',
    symptoms: [
      'Лихорадка, фарингит',
      'Увеличение лимфоузлов',
      'Гепатоспленомегалия',
      'Появление сыпи после приема амоксициллина',
      'Сыпь не является истинной аллергией'
    ],
    clinicalPresentation: 'Сыпь появляется через 1-3 дня после начала приема амоксициллина на фоне инфекционного мононуклеоза. Характеризуется макулопапулезной сыпью, напоминающей коревую.',
    complications: ['Ошибочная диагностика аллергии на пенициллины'],
    treatment: 'Отмена амоксициллина, симптоматическое лечение мононуклеоза, антигистаминные препараты',
    prevention: 'Избегание назначения амоксициллина при подозрении на инфекционный мононуклеоз',
    epidemiology: 'Возникает у 80-90% пациентов с инфекционным мононуклеозом при назначении амоксициллина.',
    pathophysiology: 'Амоксициллин вызывает иммунную реакцию у пациентов с инфекционным мононуклеозом, приводящую к развитию сыпи.',
    diagnosis: 'Клиническая картина, анамнез приема амоксициллина, серологическое исследование на ВЭБ',
    differentialDiagnosis: ['Аллергическая реакция на амоксициллин', 'Инфекционная сыпь', 'Атопический дерматит'],
    prognosis: 'Благоприятный после отмены препарата.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Макулопапулезная сыпь, напоминающая коревую',
    images: [
      '/images/atlas/amoxicillin-rash-1.jpg',
      '/images/atlas/amoxicillin-rash-2.jpg',
      '/images/atlas/amoxicillin-rash-3.jpg'
    ],
    severity: 'low',
    ageGroup: '5-25 лет',
    incubation: '4-6 недель',
    contagious: 'Несколько месяцев',
    source: 'Научная литература'
  },
  {
    name: 'Импетиго',
    category: 'бактериальные',
    description: 'Поверхностная бактериальная инфекция кожи, вызываемая стафилококками и стрептококками',
    symptoms: [
      'Поверхностные пузырьки с мутным содержимым',
      'Быстрое вскрытие пузырьков с образованием корочек',
      'Локализация на лице, руках, ногах',
      'Зуд в области высыпаний',
      'Возможна лимфаденопатия'
    ],
    clinicalPresentation: 'Заболевание начинается с появления пузырьков с мутным содержимым, которые быстро вскрываются с образованием медово-желтых корочек. Локализуется на лице, руках, ногах.',
    complications: ['Целлюлит', 'Лимфангит', 'Гломерулонефрит', 'Сепсис'],
    treatment: 'Местные антибиотики (мупироцин), системные антибиотики при распространенных формах (цефалексин, амоксициллин)',
    prevention: 'Соблюдение гигиены, обработка ссадин и порезов, избегание контакта с больными',
    epidemiology: 'Источник - больной человек, носитель. Путь передачи - контактный. Восприимчивость высокая у детей 2-6 лет.',
    pathophysiology: 'Бактерии проникают через поврежденную кожу, размножаются в поверхностных слоях эпидермиса, вызывая воспалительную реакцию.',
    diagnosis: 'Клиническая картина, бактериологическое исследование (мазок с пораженного участка)',
    differentialDiagnosis: ['Герпетическая инфекция', 'Атопический дерматит', 'Контактный дерматит'],
    prognosis: 'Благоприятный при своевременном лечении.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Пузырьки с мутным содержимым, покрытые медово-желтыми корочками',
    images: [
      '/images/atlas/impetigo-1.jpg',
      '/images/atlas/impetigo-2.jpg',
      '/images/atlas/impetigo-3.jpg'
    ],
    severity: 'medium',
    ageGroup: '2-6 лет',
    incubation: '1-3 дня',
    contagious: 'До заживления',
    source: 'Научная литература'
  },
  {
    name: 'Сыпь при менингококцемии',
    category: 'бактериальные',
    description: 'Острое инфекционное заболевание, вызываемое менингококком',
    symptoms: [
      'Высокая температура, озноб',
      'Геморрагическая сыпь',
      'Сыпь не исчезает при надавливании',
      'Быстрое прогрессирование',
      'Признаки шока'
    ],
    clinicalPresentation: 'Заболевание начинается остро с высокой лихорадки, интоксикации, появления геморрагической сыпи звездчатой формы, не исчезающей при надавливании. Быстрое прогрессирование.',
    complications: ['Сепсис', 'Менингит', 'Шок', 'ДВС-синдром'],
    treatment: 'Экстренная антибактериальная терапия (цефтриаксон, пенициллин), интенсивная терапия, дезинтоксикация',
    prevention: 'Вакцинация против менингококка, избегание контакта с больными',
    epidemiology: 'Источник - больной человек, носитель. Путь передачи - воздушно-капельный. Восприимчивость высокая у детей 1-18 лет.',
    pathophysiology: 'Менингококк проникает через слизистые оболочки, размножается в крови, вызывая эндотоксемию и поражение сосудов.',
    diagnosis: 'Клиническая картина, бактериологическое исследование (кровь, ликвор), ПЦР',
    differentialDiagnosis: ['Другие бактериальные инфекции', 'Вирусные инфекции', 'Аллергические реакции'],
    prognosis: 'Серьезный, требует немедленного лечения.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Геморрагическая сыпь звездчатой формы, не исчезающая при надавливании',
    images: [
      '/images/atlas/meningococcemia-1.jpg',
      '/images/atlas/meningococcemia-2.jpg',
      '/images/atlas/meningococcemia-3.jpg'
    ],
    severity: 'high',
    ageGroup: '1-18 лет',
    incubation: '2-10 дней',
    contagious: 'До 24 часов после начала лечения',
    source: 'Научная литература'
  }
];

export const skinRashes: Disease[] = [
  {
    name: 'Атопический дерматит',
    category: 'аллергические',
    description: 'Хроническое воспалительное заболевание кожи аллергической природы',
    symptoms: [
      'Зуд кожи',
      'Сухость и шелушение',
      'Покраснение и отек',
      'Локализация в складках кожи',
      'Рецидивирующее течение'
    ],
    clinicalPresentation: 'Хроническое воспалительное заболевание кожи, характеризующееся эритематозными бляшками с шелушением, лихенификацией. Локализуется в складках кожи, на лице.',
    complications: ['Бактериальная суперинфекция', 'Вирусная суперинфекция', 'Нарушение сна'],
    treatment: 'Топические кортикостероиды, эмоленты, антигистаминные препараты, системные препараты при тяжелых формах',
    prevention: 'Избегание триггеров, увлажнение кожи, гипоаллергенная диета',
    epidemiology: 'Распространенность 15-20% у детей. Чаще встречается в развитых странах.',
    pathophysiology: 'Нарушение барьерной функции кожи, иммунная дисрегуляция, генетическая предрасположенность.',
    diagnosis: 'Клиническая картина, критерии Ханифина-Райки, аллергологическое обследование',
    differentialDiagnosis: ['Контактный дерматит', 'Себорейный дерматит', 'Псориаз'],
    prognosis: 'Хроническое течение с периодами обострений и ремиссий.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Эритематозные бляшки с шелушением, лихенификация',
    images: [
      '/images/atlas/atopic-dermatitis-1.jpg',
      '/images/atlas/atopic-dermatitis-2.jpg',
      '/images/atlas/atopic-dermatitis-3.jpg'
    ],
    severity: 'medium',
    ageGroup: '0-18 лет',
    incubation: 'Хроническое заболевание',
    contagious: 'Не заразно',
    source: 'Научная литература'
  },
  {
    name: 'Опрелости',
    category: 'контактные',
    description: 'Воспаление кожи в результате длительного контакта с влагой',
    symptoms: [
      'Покраснение кожи в складках',
      'Мокнутие',
      'Зуд и жжение',
      'Болезненность при прикосновении',
      'Возможны трещины'
    ],
    clinicalPresentation: 'Воспаление кожи в результате длительного контакта с влагой, характеризующееся эритемой, мацерацией, возможными эрозиями в складках кожи.',
    complications: ['Бактериальная суперинфекция', 'Грибковая суперинфекция'],
    treatment: 'Подсушивающие средства, воздушные ванны, защитные кремы, при необходимости - противогрибковые препараты',
    prevention: 'Частая смена подгузников, тщательная гигиена, использование защитных кремов',
    epidemiology: 'Распространенность 50-70% у детей первого года жизни.',
    pathophysiology: 'Длительный контакт с влагой нарушает барьерную функцию кожи, способствует размножению микроорганизмов.',
    diagnosis: 'Клиническая картина, при необходимости - микробиологическое исследование',
    differentialDiagnosis: ['Атопический дерматит', 'Кандидоз', 'Себорейный дерматит'],
    prognosis: 'Благоприятный при правильном уходе.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Эритема, мацерация, возможны эрозии в складках кожи',
    images: [
      '/images/atlas/diaper-rash-1.jpg',
      '/images/atlas/diaper-rash-2.jpg',
      '/images/atlas/diaper-rash-3.jpg'
    ],
    severity: 'low',
    ageGroup: '0-2 года',
    incubation: 'Не применимо',
    contagious: 'Не заразно',
    source: 'Научная литература'
  },
  {
    name: 'Контагиозный моллюск',
    category: 'вирусные',
    description: 'Вирусное заболевание кожи, вызываемое поксвирусом',
    symptoms: [
      'Мелкие папулы с пупковидным вдавлением',
      'Безболезненные высыпания',
      'Локализация на лице, туловище, конечностях',
      'Медленное увеличение количества элементов',
      'Самопроизвольное разрешение'
    ],
    clinicalPresentation: 'Вирусное заболевание кожи, характеризующееся появлением мелких папул с пупковидным вдавлением в центре. Локализуется на лице, туловище, конечностях.',
    complications: ['Бактериальная суперинфекция', 'Распространение на другие участки'],
    treatment: 'Кюретаж, криодеструкция, местные противовирусные средства, при множественных элементах - системная терапия',
    prevention: 'Избегание контакта с больными, соблюдение гигиены',
    epidemiology: 'Источник - больной человек. Путь передачи - контактный. Восприимчивость высокая у детей 1-10 лет.',
    pathophysiology: 'Поксвирус проникает через поврежденную кожу, размножается в эпидермисе, вызывая образование узелков.',
    diagnosis: 'Клиническая картина, гистологическое исследование, ПЦР',
    differentialDiagnosis: ['Бородавки', 'Акне', 'Кисты сальных желез'],
    prognosis: 'Благоприятный, возможна спонтанная регрессия.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Мелкие папулы с пупковидным вдавлением в центре',
    images: [
      '/images/atlas/molluscum-1.jpg',
      '/images/atlas/molluscum-2.jpg',
      '/images/atlas/molluscum-3.jpg'
    ],
    severity: 'low',
    ageGroup: '1-10 лет',
    incubation: '2-7 недель',
    contagious: 'До исчезновения элементов',
    source: 'Научная литература'
  },
  {
    name: 'Пурпура Шенлейн-Геноха',
    category: 'системные',
    description: 'Системный васкулит с поражением мелких сосудов',
    symptoms: [
      'Пальпируемая пурпура',
      'Артралгии и артриты',
      'Абдоминальные боли',
      'Поражение почек',
      'Симметричные высыпания'
    ],
    clinicalPresentation: 'Системный васкулит с поражением мелких сосудов, характеризующийся пальпируемой пурпурой на разгибательных поверхностях конечностей. Часто сопровождается поражением суставов, почек, ЖКТ.',
    complications: ['Почечная недостаточность', 'Кишечные кровотечения', 'Артриты'],
    treatment: 'Симптоматическое лечение, при тяжелом течении - глюкокортикоиды, цитостатики',
    prevention: 'Специфическая профилактика не разработана',
    epidemiology: 'Распространенность 10-20 случаев на 100,000 детей. Чаще встречается у детей 2-11 лет.',
    pathophysiology: 'Иммунокомплексный васкулит с отложением IgA в стенках мелких сосудов.',
    diagnosis: 'Клиническая картина, лабораторные исследования, биопсия кожи',
    differentialDiagnosis: ['Другие васкулиты', 'Тромбоцитопеническая пурпура', 'Геморрагический васкулит'],
    prognosis: 'В большинстве случаев благоприятный, возможны рецидивы.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Пальпируемая пурпура на разгибательных поверхностях конечностей',
    images: [
      '/images/atlas/henoch-schonlein-1.jpg',
      '/images/atlas/henoch-schonlein-2.jpg',
      '/images/atlas/henoch-schonlein-3.jpg'
    ],
    severity: 'high',
    ageGroup: '2-11 лет',
    incubation: '1-3 недели',
    contagious: 'Не заразно',
    source: 'Научная литература'
  },
  {
    name: 'Псориаз',
    category: 'хронические',
    description: 'Хроническое неинфекционное заболевание кожи',
    symptoms: [
      'Розово-красные бляшки с серебристыми чешуйками',
      'Зуд различной интенсивности',
      'Симметричные высыпания',
      'Локализация на разгибательных поверхностях',
      'Рецидивирующее течение'
    ],
    clinicalPresentation: 'Хроническое неинфекционное заболевание кожи, характеризующееся розово-красными бляшками с серебристыми чешуйками, феноменом стеаринового пятна. Локализуется на разгибательных поверхностях.',
    complications: ['Псориатический артрит', 'Депрессия', 'Метаболический синдром'],
    treatment: 'Топические кортикостероиды, витамин D3 аналоги, системная терапия при тяжелом течении (метотрексат, биологические препараты)',
    prevention: 'Избегание триггеров, увлажнение кожи, здоровый образ жизни',
    epidemiology: 'Распространенность 1-3% в популяции. Чаще встречается у детей с отягощенным семейным анамнезом.',
    pathophysiology: 'Нарушение кератинизации, иммунная дисрегуляция, генетическая предрасположенность.',
    diagnosis: 'Клиническая картина, гистологическое исследование',
    differentialDiagnosis: ['Атопический дерматит', 'Себорейный дерматит', 'Контактный дерматит'],
    prognosis: 'Хроническое течение с периодами обострений и ремиссий.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Розово-красные бляшки с серебристыми чешуйками, феномен стеаринового пятна',
    images: [
      '/images/atlas/psoriasis-1.jpg',
      '/images/atlas/psoriasis-2.jpg',
      '/images/atlas/psoriasis-3.jpg'
    ],
    severity: 'medium',
    ageGroup: '5-18 лет',
    incubation: 'Хроническое заболевание',
    contagious: 'Не заразно',
    source: 'Научная литература'
  },
  {
    name: 'Ихтиоз',
    category: 'наследственные',
    description: 'Группа наследственных заболеваний, характеризующихся нарушением кератинизации',
    symptoms: [
      'Сухость и шелушение кожи',
      'Утолщение рогового слоя',
      'Трещины на коже',
      'Повышенная чувствительность к инфекциям',
      'Нарушение терморегуляции'
    ],
    clinicalPresentation: 'Группа наследственных заболеваний, характеризующихся нарушением кератинизации, сухой кожей с выраженным шелушением, напоминающим рыбью чешую.',
    complications: ['Бактериальные инфекции', 'Нарушение терморегуляции', 'Психологические проблемы'],
    treatment: 'Эмоленты, кератолитики, системные ретиноиды, при тяжелых формах - трансплантация кожи',
    prevention: 'Генетическое консультирование, пренатальная диагностика',
    epidemiology: 'Распространенность 1:100,000-1:1,000,000. Чаще встречается у детей с отягощенным семейным анамнезом.',
    pathophysiology: 'Нарушение кератинизации, мутации генов, кодирующих белки эпидермиса.',
    diagnosis: 'Клиническая картина, гистологическое исследование, генетическое тестирование',
    differentialDiagnosis: ['Атопический дерматит', 'Псориаз', 'Себорейный дерматит'],
    prognosis: 'Хроническое течение, требует пожизненного лечения.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Сухая кожа с выраженным шелушением, напоминающим рыбью чешую',
    images: [
      '/images/atlas/ichthyosis-1.jpg',
      '/images/atlas/ichthyosis-2.jpg',
      '/images/atlas/ichthyosis-3.jpg'
    ],
    severity: 'medium',
    ageGroup: '0-18 лет',
    incubation: 'Врожденное заболевание',
    contagious: 'Не заразно',
    source: 'Научная литература'
  },
  {
    name: 'Тромбоцитопеническая пурпура',
    category: 'гематологические',
    description: 'Заболевание, характеризующееся снижением количества тромбоцитов',
    symptoms: [
      'Петехиальная сыпь',
      'Кровоточивость слизистых',
      'Носовые кровотечения',
      'Кровоизлияния в кожу',
      'Повышенная кровоточивость'
    ],
    clinicalPresentation: 'Заболевание, характеризующееся снижением количества тромбоцитов, мелкопятнистыми кровоизлияниями в кожу и слизистые. Часто сопровождается кровотечениями.',
    complications: ['Кровоизлияния во внутренние органы', 'Желудочно-кишечные кровотечения', 'Внутричерепные кровоизлияния'],
    treatment: 'Глюкокортикоиды, внутривенные иммуноглобулины, при тяжелых формах - спленэктомия',
    prevention: 'Специфическая профилактика не разработана',
    epidemiology: 'Распространенность 4-5 случаев на 100,000 детей. Чаще встречается у детей 2-10 лет.',
    pathophysiology: 'Аутоиммунное разрушение тромбоцитов, нарушение их образования в костном мозге.',
    diagnosis: 'Клиническая картина, общий анализ крови, исследование костного мозга',
    differentialDiagnosis: ['Другие гематологические заболевания', 'Врожденные тромбоцитопении', 'Медикаментозные тромбоцитопении'],
    prognosis: 'В большинстве случаев благоприятный, возможны рецидивы.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Мелкопятнистые кровоизлияния в кожу и слизистые',
    images: [
      '/images/atlas/thrombocytopenia-1.jpg',
      '/images/atlas/thrombocytopenia-2.jpg',
      '/images/atlas/thrombocytopenia-3.jpg'
    ],
    severity: 'high',
    ageGroup: '2-10 лет',
    incubation: 'Острое заболевание',
    contagious: 'Не заразно',
    source: 'Научная литература'
  },
  {
    name: 'Экзема',
    category: 'воспалительные',
    description: 'Острое или хроническое воспалительное заболевание кожи',
    symptoms: [
      'Покраснение и отек кожи',
      'Везикулы с серозным содержимым',
      'Мокнутие и корки',
      'Зуд различной интенсивности',
      'Склонность к рецидивам'
    ],
    clinicalPresentation: 'Острое или хроническое воспалительное заболевание кожи, характеризующееся полиморфной сыпью: эритема, везикулы, мокнутие, корки. Локализуется в складках кожи, на лице.',
    complications: ['Бактериальная суперинфекция', 'Лихенификация', 'Нарушение сна'],
    treatment: 'Топические кортикостероиды, антигистаминные препараты, эмоленты, системные препараты при тяжелых формах',
    prevention: 'Избегание триггеров, увлажнение кожи, гипоаллергенная диета',
    epidemiology: 'Распространенность 15-20% у детей. Чаще встречается в развитых странах.',
    pathophysiology: 'Нарушение барьерной функции кожи, иммунная дисрегуляция, генетическая предрасположенность.',
    diagnosis: 'Клиническая картина, критерии Ханифина-Райки, аллергологическое обследование',
    differentialDiagnosis: ['Контактный дерматит', 'Себорейный дерматит', 'Псориаз'],
    prognosis: 'Хроническое течение с периодами обострений и ремиссий.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Полиморфная сыпь: эритема, везикулы, мокнутие, корки',
    images: [
      '/images/atlas/eczema-1.jpg',
      '/images/atlas/eczema-2.jpg',
      '/images/atlas/eczema-3.jpg'
    ],
    severity: 'medium',
    ageGroup: '0-18 лет',
    incubation: 'Острое заболевание',
    contagious: 'Не заразно',
    source: 'Научная литература'
  },
  {
    name: 'Укус комара',
    category: 'травматические',
    description: 'Реакция кожи на укус комара',
    symptoms: [
      'Папула в месте укуса',
      'Зуд различной интенсивности',
      'Покраснение и отек',
      'Возможна аллергическая реакция',
      'Самопроизвольное разрешение'
    ],
    clinicalPresentation: 'Реакция кожи на укус комара, характеризующаяся папулой или волдырем в месте укуса с окружающей эритемой. Часто сопровождается зудом.',
    complications: ['Вторичная инфекция при расчесах', 'Аллергические реакции'],
    treatment: 'Антигистаминные препараты, местные противозудные средства, при необходимости - антисептики',
    prevention: 'Использование репеллентов, защитная одежда, москитные сетки',
    epidemiology: 'Распространенность 100% в эндемичных районах. Чаще встречается в теплом климате.',
    pathophysiology: 'Реакция на слюну комара, содержащую антикоагулянты и анестетики.',
    diagnosis: 'Клиническая картина, анамнез укуса',
    differentialDiagnosis: ['Другие укусы насекомых', 'Аллергические реакции', 'Инфекционные заболевания'],
    prognosis: 'Благоприятный, обычно проходит самостоятельно.',
    references: ['AAP Red Book 2021-2024', 'Клинические рекомендации МЗ РФ 2021'],
    rashDescription: 'Папула или волдырь в месте укуса с окружающей эритемой',
    images: [
      '/images/atlas/mosquito-bite-1.jpg',
      '/images/atlas/mosquito-bite-2.jpg',
      '/images/atlas/mosquito-bite-3.jpg'
    ],
    severity: 'low',
    ageGroup: '0-18 лет',
    incubation: 'Мгновенная реакция',
    contagious: 'Не заразно',
    source: 'Научная литература'
  }
];

export const categories = [
  { value: '', label: 'Все категории' },
  { value: 'вирусные', label: 'Вирусные инфекции' },
  { value: 'бактериальные', label: 'Бактериальные инфекции' },
  { value: 'аллергические', label: 'Аллергические реакции' },
  { value: 'контактные', label: 'Контактные дерматиты' },
  { value: 'системные', label: 'Системные заболевания' },
  { value: 'хронические', label: 'Хронические заболевания' },
  { value: 'наследственные', label: 'Наследственные заболевания' },
  { value: 'гематологические', label: 'Гематологические заболевания' },
  { value: 'воспалительные', label: 'Воспалительные заболевания' },
  { value: 'травматические', label: 'Травматические поражения' },
  { value: 'лекарственные', label: 'Лекарственные реакции' }
];

// Инфекционные заболевания без сыпи
export const generalInfectiousDiseases: Disease[] = [
  {
    name: 'ОРВИ',
    category: 'вирусные',
    description: 'Острые респираторные вирусные инфекции - группа острых воспалительных заболеваний органов дыхания, вызываемых вирусами.',
    symptoms: ['Повышение температуры', 'Насморк', 'Кашель', 'Боль в горле', 'Головная боль', 'Слабость'],
    clinicalPresentation: 'Острое начало с повышения температуры, катаральных явлений со стороны верхних дыхательных путей. Интоксикация умеренная.',
    complications: ['Синусит', 'Отит', 'Бронхит', 'Пневмония'],
    treatment: 'Симптоматическое: жаропонижающие, обильное питье, постельный режим, промывание носа солевыми растворами.',
    prevention: 'Избегание контактов с больными, мытье рук, проветривание помещений, закаливание.',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный. Сезонность - осенне-зимний период.',
    pathophysiology: 'Вирусы проникают через слизистые дыхательных путей, размножаются в эпителии, вызывая воспаление.',
    diagnosis: 'Клиническая картина, ПЦР-диагностика, серологические исследования.',
    differentialDiagnosis: ['Грипп', 'Бактериальные инфекции', 'Аллергические реакции'],
    prognosis: 'Благоприятный при неосложненном течении.',
    references: ['Клинические рекомендации МЗ РФ "ОРВИ" 2021', 'AAP Red Book 2021-2024'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'low',
    ageGroup: 'все возраста',
    incubation: '1-3 дня',
    contagious: 'высокая'
  },
  {
    name: 'Грипп',
    category: 'вирусные',
    description: 'Острое инфекционное заболевание дыхательных путей, вызываемое вирусом гриппа.',
    symptoms: ['Высокая температура (39-40°C)', 'Головная боль', 'Мышечные боли', 'Слабость', 'Сухой кашель', 'Боль в горле'],
    clinicalPresentation: 'Острое начало с выраженной интоксикацией, высокой лихорадкой, головной и мышечными болями.',
    complications: ['Пневмония', 'Миокардит', 'Энцефалит', 'Синдром Рея'],
    treatment: 'Противовирусные препараты (осельтамивир, занамивир), симптоматическое лечение, постельный режим.',
    prevention: 'Вакцинация, избегание контактов с больными, мытье рук.',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный. Эпидемии в холодное время года.',
    pathophysiology: 'Вирус проникает через слизистые дыхательных путей, размножается в эпителии, вызывает системную интоксикацию.',
    diagnosis: 'Клиническая картина, ПЦР, экспресс-тесты, серологические исследования.',
    differentialDiagnosis: ['ОРВИ', 'Бактериальные инфекции', 'Другие вирусные инфекции'],
    prognosis: 'Благоприятный при неосложненном течении, серьезный при осложнениях.',
    references: ['Клинические рекомендации МЗ РФ "Грипп" 2021', 'WHO Influenza Fact Sheet 2023'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'high',
    ageGroup: 'все возраста',
    incubation: '1-4 дня',
    contagious: 'очень высокая'
  },
  {
    name: 'COVID-19',
    category: 'вирусные',
    description: 'Коронавирусная инфекция, вызываемая SARS-CoV-2, характеризующаяся поражением дыхательных путей.',
    symptoms: ['Повышение температуры', 'Кашель', 'Одышка', 'Потеря обоняния и вкуса', 'Слабость', 'Головная боль'],
    clinicalPresentation: 'От бессимптомного течения до тяжелой пневмонии с дыхательной недостаточностью.',
    complications: ['Пневмония', 'Острый респираторный дистресс-синдром', 'Тромбозы', 'Миокардит'],
    treatment: 'Симптоматическое лечение, противовирусные препараты, кислородотерапия при необходимости.',
    prevention: 'Вакцинация, ношение масок, социальное дистанцирование, мытье рук.',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный, контактный.',
    pathophysiology: 'Вирус проникает через ACE2-рецепторы, вызывает системное воспаление, поражение легких.',
    diagnosis: 'ПЦР-тест, экспресс-тесты на антиген, серологические исследования.',
    differentialDiagnosis: ['Грипп', 'ОРВИ', 'Бактериальная пневмония'],
    prognosis: 'Зависит от тяжести течения и наличия осложнений.',
    references: ['Клинические рекомендации МЗ РФ "COVID-19" 2023', 'WHO COVID-19 Fact Sheet 2023'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'high',
    ageGroup: 'все возраста',
    incubation: '2-14 дней',
    contagious: 'очень высокая'
  },
  {
    name: 'Пневмония',
    category: 'бактериальные',
    description: 'Острое инфекционное заболевание легких с поражением альвеол и развитием воспалительной экссудации.',
    symptoms: ['Высокая температура', 'Кашель с мокротой', 'Одышка', 'Боль в груди', 'Слабость', 'Потливость'],
    clinicalPresentation: 'Острое начало с лихорадкой, кашлем, одышкой. При аускультации - влажные хрипы, крепитация.',
    complications: ['Плевральный выпот', 'Абсцесс легкого', 'Сепсис', 'Дыхательная недостаточность'],
    treatment: 'Антибактериальная терапия, отхаркивающие средства, жаропонижающие, при необходимости - кислородотерапия.',
    prevention: 'Вакцинация против пневмококка, гриппа, закаливание, лечение хронических заболеваний.',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный. Чаще в холодное время года.',
    pathophysiology: 'Возбудитель проникает в альвеолы, вызывает воспаление с экссудацией, нарушает газообмен.',
    diagnosis: 'Клиническая картина, рентгенография грудной клетки, общий анализ крови, посев мокроты.',
    differentialDiagnosis: ['Бронхит', 'Туберкулез', 'Отек легких', 'Плеврит'],
    prognosis: 'Благоприятный при своевременном лечении, серьезный при осложнениях.',
    references: ['Клинические рекомендации МЗ РФ "Пневмония" 2021', 'AAP Red Book 2021-2024'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'high',
    ageGroup: 'все возраста',
    incubation: '1-3 дня',
    contagious: 'умеренная'
  },
  {
    name: 'Острый тонзиллит',
    category: 'бактериальные',
    description: 'Острое воспаление небных миндалин, чаще всего вызываемое β-гемолитическим стрептококком группы А.',
    symptoms: ['Боль в горле', 'Повышение температуры', 'Затруднение глотания', 'Головная боль', 'Слабость', 'Увеличение лимфоузлов'],
    clinicalPresentation: 'Острое начало с болью в горле, лихорадкой. При осмотре - гиперемия, отек миндалин, налеты.',
    complications: ['Паратонзиллярный абсцесс', 'Ревматическая лихорадка', 'Гломерулонефрит', 'Сепсис'],
    treatment: 'Антибактериальная терапия (пенициллины), жаропонижающие, местные антисептики, постельный режим.',
    prevention: 'Избегание контактов с больными, укрепление иммунитета, лечение хронического тонзиллита.',
    epidemiology: 'Источник - больной человек. Путь передачи - воздушно-капельный, контактный.',
    pathophysiology: 'Возбудитель проникает в миндалины, вызывает воспаление с образованием налетов.',
    diagnosis: 'Клиническая картина, мазок из зева, экспресс-тест на стрептококк, общий анализ крови.',
    differentialDiagnosis: ['Вирусный тонзиллит', 'Дифтерия', 'Инфекционный мононуклеоз'],
    prognosis: 'Благоприятный при своевременном лечении.',
    references: ['Клинические рекомендации МЗ РФ "Тонзиллит" 2021', 'AAP Red Book 2021-2024'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'medium',
    ageGroup: '3-15 лет',
    incubation: '1-3 дня',
    contagious: 'высокая'
  },
  {
    name: 'Инфекционный мононуклеоз',
    category: 'вирусные',
    description: 'Острое вирусное заболевание, вызываемое вирусом Эпштейна-Барр, характеризующееся лихорадкой, тонзиллитом, лимфаденопатией.',
    symptoms: ['Повышение температуры', 'Боль в горле', 'Увеличение лимфоузлов', 'Слабость', 'Головная боль', 'Увеличение печени и селезенки'],
    clinicalPresentation: 'Острое начало с лихорадкой, тонзиллитом, генерализованной лимфаденопатией, гепатоспленомегалией.',
    complications: ['Разрыв селезенки', 'Гепатит', 'Энцефалит', 'Гемолитическая анемия'],
    treatment: 'Симптоматическое лечение, постельный режим, избегание физических нагрузок, при необходимости - кортикостероиды.',
    prevention: 'Избегание контактов с больными, не целовать детей в губы.',
    epidemiology: 'Источник - больной человек. Путь передачи - контактный, через слюну. Чаще у детей и подростков.',
    pathophysiology: 'Вирус поражает В-лимфоциты, вызывает их пролиферацию, что приводит к лимфаденопатии.',
    diagnosis: 'Клиническая картина, общий анализ крови (атипичные мононуклеары), серологические исследования.',
    differentialDiagnosis: ['Стрептококковый тонзиллит', 'Цитомегаловирусная инфекция', 'Токсоплазмоз'],
    prognosis: 'Благоприятный, но длительное течение (2-4 недели).',
    references: ['Клинические рекомендации МЗ РФ "Мононуклеоз" 2021', 'AAP Red Book 2021-2024'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'medium',
    ageGroup: '5-25 лет',
    incubation: '4-6 недель',
    contagious: 'умеренная'
  },
  {
    name: 'Вирусный гастроэнтерит',
    category: 'вирусные',
    description: 'Острое воспалительное заболевание желудка и тонкого кишечника, чаще всего вызываемое ротавирусами и норовирусами.',
    symptoms: ['Тошнота', 'Рвота', 'Диарея', 'Боль в животе', 'Повышение температуры', 'Слабость'],
    clinicalPresentation: 'Острое начало с тошноты, рвоты, диареи. Стул водянистый, без патологических примесей.',
    complications: ['Обезвоживание', 'Электролитные нарушения', 'Судороги', 'Острая почечная недостаточность'],
    treatment: 'Оральная регидратация, диета, противорвотные средства, пробиотики.',
    prevention: 'Вакцинация против ротавируса, соблюдение гигиены, мытье рук.',
    epidemiology: 'Источник - больной человек. Путь передачи - фекально-оральный, контактный.',
    pathophysiology: 'Вирус поражает энтероциты, нарушает всасывание, вызывает секреторную диарею.',
    diagnosis: 'Клиническая картина, экспресс-тесты на ротавирус, общий анализ крови.',
    differentialDiagnosis: ['Бактериальные кишечные инфекции', 'Пищевые отравления', 'Аппендицит'],
    prognosis: 'Благоприятный при адекватной регидратации.',
    references: ['Клинические рекомендации МЗ РФ "Гастроэнтерит" 2021', 'WHO Diarrhoea Fact Sheet 2023'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'medium',
    ageGroup: 'все возраста',
    incubation: '1-3 дня',
    contagious: 'высокая'
  },
  {
    name: 'Шигеллез',
    category: 'бактериальные',
    description: 'Острая кишечная инфекция, вызываемая бактериями рода Shigella, характеризующаяся поражением толстого кишечника.',
    symptoms: ['Повышение температуры', 'Схваткообразные боли в животе', 'Тенезмы', 'Стул с примесью крови и слизи', 'Тошнота', 'Рвота'],
    clinicalPresentation: 'Острое начало с лихорадкой, схваткообразными болями в животе, частым жидким стулом с примесью крови и слизи.',
    complications: ['Обезвоживание', 'Токсический мегаколон', 'Перфорация кишечника', 'Сепсис'],
    treatment: 'Антибактериальная терапия (фторхинолоны, цефалоспорины), оральная регидратация, диета.',
    prevention: 'Соблюдение гигиены, мытье рук, безопасная пища и вода.',
    epidemiology: 'Источник - больной человек. Путь передачи - фекально-оральный.',
    pathophysiology: 'Бактерии проникают в эпителий толстого кишечника, вызывают воспаление и изъязвления.',
    diagnosis: 'Клиническая картина, посев кала, ПЦР, общий анализ крови.',
    differentialDiagnosis: ['Сальмонеллез', 'Амебиаз', 'Неспецифический язвенный колит'],
    prognosis: 'Благоприятный при своевременном лечении.',
    references: ['Клинические рекомендации МЗ РФ "Шигеллез" 2021', 'WHO Shigellosis Fact Sheet 2023'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'high',
    ageGroup: 'все возраста',
    incubation: '1-7 дней',
    contagious: 'высокая'
  },
  {
    name: 'Сальмонеллез',
    category: 'бактериальные',
    description: 'Острая кишечная инфекция, вызываемая бактериями рода Salmonella, характеризующаяся поражением желудочно-кишечного тракта.',
    symptoms: ['Повышение температуры', 'Тошнота', 'Рвота', 'Диарея', 'Боль в животе', 'Слабость'],
    clinicalPresentation: 'Острое начало с лихорадкой, тошнотой, рвотой, диареей. Стул зловонный, зеленоватый.',
    complications: ['Обезвоживание', 'Сепсис', 'Менингит', 'Остеомиелит'],
    treatment: 'Антибактериальная терапия (фторхинолоны, цефалоспорины), оральная регидратация, диета.',
    prevention: 'Соблюдение гигиены, термическая обработка продуктов, мытье рук.',
    epidemiology: 'Источник - больные животные, зараженные продукты. Путь передачи - алиментарный.',
    pathophysiology: 'Бактерии проникают в эпителий кишечника, вызывают воспаление и нарушение всасывания.',
    diagnosis: 'Клиническая картина, посев кала, крови, ПЦР, общий анализ крови.',
    differentialDiagnosis: ['Шигеллез', 'Вирусный гастроэнтерит', 'Пищевые отравления'],
    prognosis: 'Благоприятный при неосложненном течении.',
    references: ['Клинические рекомендации МЗ РФ "Сальмонеллез" 2021', 'WHO Salmonella Fact Sheet 2023'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'medium',
    ageGroup: 'все возраста',
    incubation: '6-72 часа',
    contagious: 'умеренная'
  }
];

// Общая педиатрия
export const generalPediatrics: Disease[] = [
  {
    name: 'Железодефицитная анемия',
    category: 'гематологические',
    description: 'Синдром, характеризующийся нарушением синтеза гемоглобина вследствие дефицита железа.',
    symptoms: ['Бледность кожи', 'Слабость', 'Утомляемость', 'Головокружение', 'Одышка при нагрузке', 'Ломкость ногтей'],
    clinicalPresentation: 'Постепенное развитие симптомов. Бледность кожи и слизистых, тахикардия, систолический шум.',
    complications: ['Задержка физического развития', 'Снижение иммунитета', 'Нарушение когнитивных функций'],
    treatment: 'Препараты железа, диета с высоким содержанием железа, лечение основного заболевания.',
    prevention: 'Профилактический прием железа у групп риска, рациональное питание.',
    epidemiology: 'Распространенность 20-40% у детей раннего возраста.',
    pathophysiology: 'Дефицит железа нарушает синтез гемоглобина, снижает кислородтранспортную функцию крови.',
    diagnosis: 'Общий анализ крови, ферритин, ОЖСС, клиническая картина.',
    differentialDiagnosis: ['В12-дефицитная анемия', 'Гемолитическая анемия', 'Апластическая анемия'],
    prognosis: 'Благоприятный при своевременном лечении.',
    references: ['Клинические рекомендации МЗ РФ "Анемия" 2021', 'AAP Iron Deficiency Anemia Guidelines 2023'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'medium',
    ageGroup: '6 месяцев - 3 года',
    incubation: 'не применимо',
    contagious: 'нет'
  },
  {
    name: 'Запор',
    category: 'функциональные',
    description: 'Нарушение функции кишечника, характеризующееся редким, затрудненным или неполным опорожнением кишечника.',
    symptoms: ['Редкий стул (менее 3 раз в неделю)', 'Твердый стул', 'Натуживание при дефекации', 'Боль в животе', 'Вздутие живота'],
    clinicalPresentation: 'Редкий, твердый стул, натуживание при дефекации, боль в животе, метеоризм.',
    complications: ['Каловые камни', 'Энкопрез', 'Трещины ануса', 'Геморрой'],
    treatment: 'Диета с высоким содержанием клетчатки, достаточное потребление жидкости, слабительные средства.',
    prevention: 'Рациональное питание, достаточная физическая активность, режим дефекации.',
    epidemiology: 'Распространенность 15-30% у детей.',
    pathophysiology: 'Нарушение моторики кишечника, снижение чувствительности рецепторов прямой кишки.',
    diagnosis: 'Клиническая картина, анамнез, при необходимости - рентгенологическое исследование.',
    differentialDiagnosis: ['Болезнь Гиршпрунга', 'Гипотиреоз', 'Сахарный диабет'],
    prognosis: 'Благоприятный при комплексном лечении.',
    references: ['Клинические рекомендации МЗ РФ "Запор" 2021', 'NASPGHAN Constipation Guidelines 2023'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'low',
    ageGroup: 'все возраста',
    incubation: 'не применимо',
    contagious: 'нет'
  },
  {
    name: 'Головная боль напряжения',
    category: 'неврологические',
    description: 'Первичная головная боль, характеризующаяся двусторонней, давящей, непульсирующей болью.',
    symptoms: ['Двусторонняя головная боль', 'Давящий характер боли', 'Умеренная интенсивность', 'Отсутствие тошноты', 'Светобоязнь (редко)'],
    clinicalPresentation: 'Двусторонняя, давящая головная боль умеренной интенсивности, без тошноты и рвоты.',
    complications: ['Хроническая головная боль', 'Депрессия', 'Тревожность'],
    treatment: 'НПВС, миорелаксанты, физиотерапия, психотерапия, коррекция образа жизни.',
    prevention: 'Избегание стрессов, регулярный сон, физическая активность, правильная осанка.',
    epidemiology: 'Распространенность 10-25% у детей и подростков.',
    pathophysiology: 'Напряжение мышц головы и шеи, нарушение болевых механизмов.',
    diagnosis: 'Клиническая картина, исключение вторичных причин, при необходимости - нейровизуализация.',
    differentialDiagnosis: ['Мигрень', 'Синусит', 'Повышение внутричерепного давления'],
    prognosis: 'Благоприятный при комплексном лечении.',
    references: ['Клинические рекомендации МЗ РФ "Головная боль" 2021', 'IHS Classification 2021'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'low',
    ageGroup: '7-18 лет',
    incubation: 'не применимо',
    contagious: 'нет'
  },
  {
    name: 'Мигрень',
    category: 'неврологические',
    description: 'Первичная головная боль, характеризующаяся приступами пульсирующей боли, часто односторонней.',
    symptoms: ['Пульсирующая головная боль', 'Односторонняя локализация', 'Тошнота и рвота', 'Светобоязнь', 'Звукобоязнь', 'Аура (у 20%)'],
    clinicalPresentation: 'Приступы пульсирующей головной боли с тошнотой, рвотой, свето- и звукобоязнью.',
    complications: ['Хроническая мигрень', 'Мигренозный статус', 'Депрессия', 'Тревожность'],
    treatment: 'Триптаны, НПВС, противорвотные, профилактическая терапия, коррекция образа жизни.',
    prevention: 'Избегание триггеров, регулярный сон, физическая активность, профилактические препараты.',
    epidemiology: 'Распространенность 3-10% у детей, чаще у девочек.',
    pathophysiology: 'Нарушение сосудистого тонуса, активация тригеминоваскулярной системы.',
    diagnosis: 'Клиническая картина, критерии IHS, исключение вторичных причин.',
    differentialDiagnosis: ['Головная боль напряжения', 'Кластерная головная боль', 'Вторичные головные боли'],
    prognosis: 'Благоприятный при адекватном лечении.',
    references: ['Клинические рекомендации МЗ РФ "Мигрень" 2021', 'IHS Classification 2021'],
    rashDescription: 'Сыпь не характерна',
    images: [],
    severity: 'medium',
    ageGroup: '5-18 лет',
    incubation: 'не применимо',
    contagious: 'нет'
  }
];
